import React, { useState, useEffect, useCallback, useRef } from 'react';
import { urls } from './config.dev';
import GiveFPmodal from './GiveFPmodal';
import { useNavigate } from 'react-router-dom';
import Topbar from './Topbar';

// ─── DESIGN TOKENS ───────────────────────────────────────────────────────────
const colors = {
  // Blue theme - Dark sidebar with vibrant accents
  blue: {
    sidebarBg: '#0a1e4a',
    sidebarBorder: '#1e3a8a',
    activeNavBg: '#2563eb',
    activeNavText: '#ffffff',
    inactiveNavText: '#e0e7ff',
    navHoverBg: '#1e3a8a',
    sectionHeaderText: '#94a3b8',
    mainBg: '#f8fafc',
    cardBg: '#ffffff',
    cardBorder: '#e2e8f0',
    accent: '#16a34a',
    accentLight: '#dcfce7',
    danger: '#dc2626',
    dangerLight: '#fef2f2',
    warning: '#d97706',
    warningLight: '#fffbeb',
    info: '#2563eb',
    infoLight: '#eff6ff',
    skyBlue: '#38bdf8',
    skyBlueLight: '#e0f2fe',
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#64748b',
    tableHeader: '#f1f5f9',
    tableBorder: '#e2e8f0',
    badgeGreen: { bg: '#dcfce7', text: '#166534' },
    badgeRed: { bg: '#fee2e2', text: '#991b1b' },
    badgeOrange: { bg: '#ffedd5', text: '#9a3412' },
    badgeBlue: { bg: '#dbeafe', text: '#1e40af' },
    badgeSky: { bg: '#e0f2fe', text: '#0369a1' },
    badgeGray: { bg: '#f1f5f9', text: '#475569' },
    filterSection: '#0d2257',
    collapseButtonBg: '#1e3a8a',
    collapseButtonHover: '#2563eb',
    collapseButtonText: '#ffffff',
    tooltipBg: '#1e293b',
    tooltipText: '#ffffff',
    iconBright: '#fbbf24',
    iconHover: '#f59e0b',
    sidebarText: '#ffffff',
    sidebarTextMuted: '#a5b4fc',
    staffCardBg: 'rgba(255,255,255,0.05)',
    staffCardBorder: '#1e3a8a',
    logoBg: '#2563eb',
  },
  // White theme - Light sidebar with dark text
  white: {
    sidebarBg: '#ffffff',
    sidebarBorder: '#e2e8f0',
    activeNavBg: '#f1f5f9',
    activeNavText: '#0f172a',
    inactiveNavText: '#475569',
    navHoverBg: '#f8fafc',
    sectionHeaderText: '#64748b',
    mainBg: '#f8fafc',
    cardBg: '#ffffff',
    cardBorder: '#e2e8f0',
    accent: '#16a34a',
    accentLight: '#dcfce7',
    danger: '#dc2626',
    dangerLight: '#fef2f2',
    warning: '#d97706',
    warningLight: '#fffbeb',
    info: '#2563eb',
    infoLight: '#eff6ff',
    skyBlue: '#38bdf8',
    skyBlueLight: '#e0f2fe',
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#64748b',
    tableHeader: '#f1f5f9',
    tableBorder: '#e2e8f0',
    badgeGreen: { bg: '#dcfce7', text: '#166534' },
    badgeRed: { bg: '#fee2e2', text: '#991b1b' },
    badgeOrange: { bg: '#ffedd5', text: '#9a3412' },
    badgeBlue: { bg: '#dbeafe', text: '#1e40af' },
    badgeSky: { bg: '#e0f2fe', text: '#0369a1' },
    badgeGray: { bg: '#f1f5f9', text: '#475569' },
    filterSection: '#f8fafc',
    collapseButtonBg: '#e2e8f0',
    collapseButtonHover: '#cbd5e1',
    collapseButtonText: '#0f172a',
    tooltipBg: '#1e293b',
    tooltipText: '#ffffff',
    iconBright: '#f59e0b',
    iconHover: '#d97706',
    sidebarText: '#0f172a',
    sidebarTextMuted: '#64748b',
    staffCardBg: '#f8fafc',
    staffCardBorder: '#e2e8f0',
    logoBg: '#2563eb',
  }
};

// ─── SHARED STYLES ────────────────────────────────────────────────────────────
const styles = (theme) => ({
  card: {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
  },
  navItem: (active, collapsed) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: collapsed ? 'center' : 'flex-start',
    gap: collapsed ? '0' : '12px',
    padding: collapsed ? '12px 0' : '10px 14px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13.5px',
    fontWeight: active ? '600' : '500',
    color: active ? theme.activeNavText : theme.inactiveNavText,
    background: active ? theme.activeNavBg : 'transparent',
    transition: 'all 0.15s ease',
    textDecoration: 'none',
    border: 'none',
    width: '100%',
    textAlign: collapsed ? 'center' : 'left',
    marginBottom: '2px',
    position: 'relative',
  }),
  sectionHeader: (collapsed) => ({
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.sectionHeaderText,
    padding: collapsed ? '0' : '0 14px',
    marginBottom: '8px',
    marginTop: '12px',
    textAlign: collapsed ? 'center' : 'left',
  }),
  tooltip: {
    position: 'absolute',
    left: '100%',
    top: '50%',
    transform: 'translateY(-50%)',
    marginLeft: '12px',
    padding: '8px 12px',
    background: theme.tooltipBg,
    color: theme.tooltipText,
    fontSize: '12px',
    fontWeight: '500',
    borderRadius: '6px',
    whiteSpace: 'nowrap',
    zIndex: 1000,
    pointerEvents: 'none',
    boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
    border: `1px solid ${theme.sidebarBorder}`,
  },
  collapseButton: {
    background: theme.collapseButtonBg,
    border: 'none',
    color: theme.collapseButtonText,
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
    padding: '10px 14px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    border: '1px solid rgba(0,0,0,0.05)',
    minWidth: '40px',
    height: '40px',
  },
  badge: (type) => {
    const map = {
      green: theme.badgeGreen,
      red: theme.badgeRed,
      orange: theme.badgeOrange,
      blue: theme.badgeBlue,
      sky: theme.badgeSky,
      gray: theme.badgeGray,
    };
    const c = map[type] || map.gray;
    return {
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '3px 10px', borderRadius: '20px', fontSize: '11.5px', fontWeight: '600',
      background: c.bg, color: c.text,
    };
  },
  statCard: {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    transition: 'all 0.2s ease',
  },
  panel: {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  panelHeader: {
    padding: '16px 20px',
    borderBottom: `1px solid ${theme.tableBorder}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  panelTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: theme.textPrimary,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  patientCard: {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '10px',
    transition: 'all 0.15s ease',
  },
  selectButton: {
    padding: '6px 14px',
    borderRadius: '20px',
    border: `1px solid ${theme.accent}`,
    background: 'transparent',
    color: theme.accent,
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  methodRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: '8px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(3px)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: '12px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    overflow: 'hidden',
  },
});

// ─── Global Styles Component ─────────────────────────────────────────────────
const GlobalStyles = ({ theme }) => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    
    * { 
      box-sizing: border-box; 
      margin: 0; 
      padding: 0; 
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    
    ::-webkit-scrollbar { width: 6px; height: 6px; }
    ::-webkit-scrollbar-track { background: ${theme.tableHeader}; }
    ::-webkit-scrollbar-thumb { background: ${theme.textMuted}; border-radius: 4px; }
    ::-webkit-scrollbar-thumb:hover { background: ${theme.textSecondary}; }
    
    .nav-item:hover { 
      background: ${theme.navHoverBg} !important; 
      color: ${theme.sidebarText} !important; 
    }
    
    .nav-icon { 
      color: ${theme.iconBright}; 
      font-size: 18px; 
      transition: all 0.2s ease; 
    }
    
    .nav-item:hover .nav-icon { 
      color: ${theme.iconHover}; 
      transform: scale(1.1); 
    }
    
    .active-tab { 
      background: ${theme.activeNavBg} !important; 
      border-radius: 8px !important;
    }
    
    .active-tab .nav-icon { 
      color: ${theme.activeNavText} !important; 
    }
    
    .collapse-btn:hover { 
      background: ${theme.collapseButtonHover} !important; 
      transform: scale(1.05); 
      box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
    }
    
    .patient-card:hover {
      border-color: ${theme.accent} !important;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    }
    
    .select-btn:hover {
      background: ${theme.accent} !important;
      color: #ffffff !important;
    }
    
    .method-row:hover {
      border-color: ${theme.accent} !important;
      background: ${theme.accentLight};
    }
    
    .stat-card:hover {
      border-color: ${theme.cardBorder} !important;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    }
    
    .fade-in { 
      animation: fadeIn 0.3s ease; 
    }
    
    @keyframes fadeIn { 
      from { opacity: 0; transform: translateY(6px); } 
      to { opacity: 1; transform: translateY(0); } 
    }
    
    @keyframes spin { 
      to { transform: rotate(360deg); } 
    }
    
    .spinning span { 
      animation: spin 0.8s linear; 
    }
    
    @media (max-width: 768px) {
      .stats-grid {
        grid-template-columns: 1fr !important;
      }
      .columns-grid {
        grid-template-columns: 1fr !important;
      }
    }
  `}</style>
);

// ─── Clock Component ────────────────────────────────────────────────────────
function LiveClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span style={{ fontSize: '13px', color: colors.white.textMuted, fontVariantNumeric: 'tabular-nums' }}>
      {time.toLocaleDateString('en-UG', { weekday: 'short', day: 'numeric', month: 'short' })}
      {' · '}
      {time.toLocaleTimeString('en-UG', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
    </span>
  );
}

// ─── Loading Spinner ────────────────────────────────────────────────────────
function LoadingSpinner({ theme }) {
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '400px', 
      gap: '16px' 
    }}>
      <div style={{ 
        width: '40px', 
        height: '40px', 
        border: `3px solid ${theme.tableBorder}`, 
        borderTopColor: theme.accent, 
        borderRadius: '50%', 
        animation: 'spin 1s linear infinite' 
      }}></div>
      <div style={{ color: theme.textMuted, fontSize: '14px' }}>Loading dashboard...</div>
    </div>
  );
}

// ─── Notification Component ─────────────────────────────────────────────────
const Notification = ({ message, type, onDismiss, theme }) => {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(), 4000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div style={{
      background: theme.cardBg,
      border: `1px solid ${theme.cardBorder}`,
      borderRadius: '8px',
      padding: '14px 18px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      borderLeft: `4px solid ${type === 'success' ? theme.accent : theme.danger}`,
      animation: 'fadeIn 0.3s ease',
    }}>
      <span style={{ fontSize: '18px' }}>
        {type === 'success' ? '✓' : '⚠'}
      </span>
      <span style={{ fontSize: '13px', color: theme.textPrimary }}>{message}</span>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────
function NurseDashboard() {
  const [fpPatients, setFpPatients] = useState([]);
  const [familyPlanningMethods, setFamilyPlanningMethods] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [clientStatuses, setClientStatuses] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [employeeName, setEmployeeName] = useState('');
  const [activeSection, setActiveSection] = useState('fp');
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [hoveredNavItem, setHoveredNavItem] = useState(null);
  const [currentTheme, setCurrentTheme] = useState('blue'); // default theme

  // New state variables for expiry and batch settings from security response
  const [useDrugExpiryDate, setUseDrugExpiryDate] = useState('yes');
  const [useDrugBatchNumbers, setUseDrugBatchNumbers] = useState('yes');

  const intervalRef = useRef(null);
  const isMounted = useRef(true);

  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token') || '';

  // Get the active theme colors
  const theme = colors[currentTheme];

  // ── Notification helpers ─────────────────────────────────────────────────
  const addNotification = (message, type = 'success') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // ── Security check with theme detection and capture expiry/batch settings ──
  useEffect(() => {
    if (!urlToken) { navigate('/login'); return; }
    const check = async () => {
      try {
        const res = await fetch(urls.security, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: urlToken }),
        });
        if (!res.ok) throw new Error('Security check failed');
        const data = await res.json();
        if (data.message === 'Session valid') {
          setEmployeeName(data.employee_name || '');
          
          // Check theme color from security response
          const themeColor = data.colour || '';
          if (!themeColor || themeColor.toLowerCase() === 'white' || themeColor.toLowerCase() === 'null') {
            setCurrentTheme('white');
          } else {
            setCurrentTheme('blue');
          }
          
          // Capture expiry and batch settings from security response
          setUseDrugExpiryDate(data.use_drug_expiry_date || 'yes');
          setUseDrugBatchNumbers(data.use_drug_batch_numbers || 'yes');
          
          localStorage.setItem('token', data.clinic_session_token);
        } else if (data.error === 'Session expired') {
          navigate(`/dashboard?token=${data.clinic_session_token}`);
        } else {
          navigate('/login');
        }
      } catch {
        navigate('/login');
      }
    };
    check();
  }, [navigate, urlToken]);

  // ── Fetch helpers ─────────────────────────────────────────────────────────
  const fetchFPPatients = useCallback(async () => {
    if (!urlToken) return;
    try {
      const res = await fetch(urls.fetchFPwaiting, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: urlToken }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (isMounted.current) {
        if (data.message) { setFpPatients([]); }
        else { setFpPatients(Array.isArray(data.contacts) ? data.contacts : []); }
      }
    } catch {
      if (isMounted.current) {
        setErrorMessage('Unable to load patient data. Retrying…');
      }
    }
  }, [urlToken]);

  const fetchMethods = useCallback(async () => {
    if (!urlToken) return;
    try {
      const res = await fetch(urls.allFamilyPlanningMethods, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: urlToken }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (isMounted.current) {
        setFamilyPlanningMethods(Array.isArray(data) ? data : []);
      }
    } catch {
      // silent
    }
  }, [urlToken]);

  const fetchStatuses = useCallback(async () => {
    if (!urlToken) return;
    try {
      const res = await fetch(urls.fetchFPstatus, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: urlToken }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (isMounted.current) {
        if (data.message) { setClientStatuses([]); }
        else { setClientStatuses(Array.isArray(data.contacts) ? data.contacts : []); }
      }
    } catch {
      if (isMounted.current) {
        setErrorMessage('Unable to load client statuses. Retrying…');
      }
    }
  }, [urlToken]);

  const refreshAll = useCallback(async (showSpinner = false) => {
    if (showSpinner) setIsRefreshing(true);
    await Promise.allSettled([fetchFPPatients(), fetchStatuses()]);
    if (showSpinner && isMounted.current) setIsRefreshing(false);
  }, [fetchFPPatients, fetchStatuses]);

  // ── Init + polling ────────────────────────────────────────────────────────
  useEffect(() => {
    isMounted.current = true;

    const init = async () => {
      setLoading(true);
      await Promise.allSettled([fetchFPPatients(), fetchMethods(), fetchStatuses()]);
      if (isMounted.current) {
        setLoading(false);
      }
    };

    init();

    intervalRef.current = setInterval(() => {
      refreshAll(false);
    }, 10000);

    return () => {
      isMounted.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchFPPatients, fetchMethods, fetchStatuses, refreshAll]);

  // ── Action handlers ───────────────────────────────────────────────────────
  const submitMethod = async (fpId, methodName) => {
    if (!fpId || !methodName) {
      addNotification('Missing required information', 'error');
      return;
    }

    try {
      const res = await fetch(urls.payfp, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: urlToken, fpId, methodName }),
      });

      if (!res.ok) throw new Error();

      addNotification('Submitted! Please send the client to the cashier.', 'success');
      setSelectedPatient(null);
      refreshAll();
    } catch {
      addNotification('Submission failed. Please try again.', 'error');
    }
  };

  const handleMethodSelect = (methodName) => {
    if (!selectedPatient || !selectedPatient.fp_id) {
      addNotification('No patient selected', 'error');
      return;
    }

    const msg = `Confirm "${methodName}" for ${selectedPatient.first_name || ''} ${selectedPatient.last_name || ''}?`;
    if (window.confirm(msg)) {
      submitMethod(selectedPatient.fp_id, methodName);
    }
  };

  const handleOpenModal = (client) => {
    if (!client || !client.contact_id) {
      addNotification('Invalid client data', 'error');
      return;
    }
    // Pass the expiry and batch settings to the modal along with client data
    setModalData({ 
      ...client, 
      token: urlToken,
      use_drug_expiry_date: useDrugExpiryDate,
      use_drug_batch_numbers: useDrugBatchNumbers
    });
    setShowModal(true);
  };

  // ── Derived stats ─────────────────────────────────────────────────────
  const waitingCount = Array.isArray(fpPatients) ? fpPatients.length : 0;
  const giveCount = Array.isArray(clientStatuses) ? clientStatuses.filter(c => c?.status?.includes('Give')).length : 0;
  const doneCount = Array.isArray(clientStatuses) ? clientStatuses.filter(c => !c?.status?.includes('Give')).length : 0;

  // ── Avatar initials ───────────────────────────────────────────────────────
  const initials = (name) => {
    if (!name) return 'N';
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name[0].toUpperCase();
  };

  // Navigation sections
  const navSections = [
    {
      label: 'FAMILY PLANNING',
      items: [
        { id: 'fp', icon: '🩺', label: 'FP Dashboard', action: () => setActiveSection('fp'), badge: waitingCount },
      ],
    },
    {
      label: 'MODULES',
      items: [
        { id: 'maternity', icon: '🤱', label: 'Maternity Section', action: () => navigate(`/maternity-dashboard/?token=${urlToken}`) },
        { id: 'files', icon: '📂', label: 'Patient Files', action: () => navigate(`/patientfiles2/?token=${urlToken}`) },
      ],
    },
  ];

  if (loading) {
    return (
      <>
        <GlobalStyles theme={theme} />
        <div style={{ display: 'flex', minHeight: '100vh', background: theme.mainBg }}>
          <aside style={{
            width: sidebarCollapsed ? '80px' : '260px',
            background: theme.sidebarBg,
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            transition: 'width 0.3s ease',
          }}>
            <div style={{ padding: '28px 24px' }}>
              <h1 style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.1rem', fontWeight: 800, color: theme.sidebarText }}>
                <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: theme.accent, marginRight: '6px' }} />
                ClinicOS
              </h1>
            </div>
          </aside>
          <main style={{ marginLeft: '260px', flex: 1 }}>
            <Topbar token={urlToken} themeColor={currentTheme} />
            <div style={{ padding: '28px' }}>
              <LoadingSpinner theme={theme} />
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <GlobalStyles theme={theme} />
      <div style={{ display: 'flex', minHeight: '100vh', background: theme.mainBg, position: 'relative' }}>

        {/* Notification Container */}
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          maxWidth: '350px',
        }}>
          {notifications.map(notification => (
            <Notification
              key={notification.id}
              message={notification.message}
              type={notification.type}
              onDismiss={() => removeNotification(notification.id)}
              theme={theme}
            />
          ))}
        </div>

        {/* ─── Sidebar ─────────────────────────────────────────────────── */}
        <aside style={{
          width: sidebarCollapsed ? '80px' : '260px',
          background: theme.sidebarBg,
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          borderRight: `1px solid ${theme.sidebarBorder}`,
          position: 'fixed',
          paddingTop: '80px',
          top: 0,
          left: 0,
          bottom: 0,
          overflowY: 'auto',
          boxShadow: currentTheme === 'blue' ? '2px 0 8px rgba(0,0,0,0.1)' : '2px 0 8px rgba(0,0,0,0.05)',
          transition: 'width 0.3s ease',
          zIndex: 900,
        }}>
          {/* Logo with Collapse Button */}
          <div style={{
            padding: sidebarCollapsed ? '20px 10px' : '20px 16px',
            borderBottom: `2px solid ${theme.sidebarBorder}`,
            display: 'flex',
            flexDirection: sidebarCollapsed ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: currentTheme === 'blue' ? 'rgba(0,0,0,0.2)' : theme.filterSection,
            minHeight: sidebarCollapsed ? '120px' : '80px',
          }}>
            {/* Logo section - CP */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flex: 1,
              order: sidebarCollapsed ? 2 : 1,
              marginTop: sidebarCollapsed ? '12px' : 0,
            }}>
              <div style={{
                width: '45px',
                height: '45px',
                background: theme.logoBg,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                flexShrink: 0,
                color: '#ffffff',
                fontWeight: 'bold',
                boxShadow: currentTheme === 'blue' ? '0 4px 10px rgba(37, 99, 235, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                CP
              </div>
              {!sidebarCollapsed && (
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ color: theme.sidebarText, fontWeight: '700', fontSize: '16px', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>ClinicPro</div>
                  <div style={{ color: theme.sidebarTextMuted, fontSize: '11px', fontWeight: '500', marginTop: '2px', whiteSpace: 'nowrap' }}>Nurse Workstation</div>
                </div>
              )}
            </div>

            {/* Collapse Button */}
            <button
              className="collapse-btn"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              style={{
                ...styles(theme).collapseButton,
                background: theme.collapseButtonBg,
                border: currentTheme === 'blue' ? '2px solid rgba(255,255,255,0.2)' : '2px solid rgba(0,0,0,0.05)',
                color: theme.collapseButtonText,
                width: '42px',
                height: '42px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '20px',
                fontWeight: 'bold',
                boxShadow: currentTheme === 'blue' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
                order: sidebarCollapsed ? 1 : 2,
                marginLeft: sidebarCollapsed ? 0 : 'auto',
                flexShrink: 0
              }}
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {sidebarCollapsed ? '→' : '←'}
            </button>
          </div>

          {/* Staff Card - Only when not collapsed */}
          {!sidebarCollapsed && employeeName && (
            <div style={{
              margin: '16px 16px 20px',
              background: theme.staffCardBg,
              borderRadius: '10px',
              padding: '14px 16px',
              border: `1px solid ${theme.staffCardBorder}`,
            }}>
              <div style={{
                width: '38px',
                height: '38px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.accent}, ${theme.logoBg})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '700',
                color: '#fff',
                marginBottom: '10px',
              }}>
                {initials(employeeName)}
              </div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: theme.sidebarText }}>{employeeName}</div>
              <div style={{ fontSize: '11px', color: theme.sidebarTextMuted, marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Registered Nurse
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav style={{ flex: 1, padding: sidebarCollapsed ? '12px 0' : '8px 12px', overflowY: 'auto' }}>
            {navSections.map(section => (
              <div key={section.label} style={{ marginBottom: '16px' }}>
                {!sidebarCollapsed && <div style={styles(theme).sectionHeader(sidebarCollapsed)}>{section.label}</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  {section.items.map(item => {
                    const isActive = item.id === activeSection;
                    return (
                      <button
                        key={item.id}
                        className={`nav-item ${isActive ? 'active-tab' : ''}`}
                        onClick={item.action}
                        onMouseEnter={() => setHoveredNavItem(item.id)}
                        onMouseLeave={() => setHoveredNavItem(null)}
                        style={{
                          ...styles(theme).navItem(isActive, sidebarCollapsed),
                          padding: sidebarCollapsed ? '12px 0' : '10px 14px',
                          margin: sidebarCollapsed ? '0' : '0 0 2px 0',
                          position: 'relative',
                        }}
                      >
                        <span className="nav-icon" style={{
                          fontSize: '18px',
                          width: '20px',
                          textAlign: 'center',
                          filter: currentTheme === 'blue' ? 'brightness(1.2)' : 'none',
                          textShadow: currentTheme === 'blue' ? '0 0 5px rgba(251, 191, 36, 0.3)' : 'none',
                          color: isActive ? theme.activeNavText : theme.iconBright,
                        }}>
                          {item.icon}
                        </span>
                        {!sidebarCollapsed && (
                          <>
                            <span style={{ fontWeight: '500', flex: 1, textAlign: 'left', color: isActive ? theme.activeNavText : theme.sidebarText }}>
                              {item.label}
                            </span>
                            {item.badge > 0 && (
                              <span style={{
                                background: theme.danger,
                                color: '#fff',
                                fontSize: '10px',
                                fontWeight: '700',
                                padding: '2px 6px',
                                borderRadius: '10px',
                                minWidth: '18px',
                                textAlign: 'center',
                              }}>
                                {item.badge}
                              </span>
                            )}
                          </>
                        )}

                        {/* Tooltip for collapsed mode */}
                        {sidebarCollapsed && hoveredNavItem === item.id && (
                          <div style={styles(theme).tooltip}>
                            {item.label}
                            {item.badge > 0 && ` (${item.badge})`}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User Footer - Collapsed/Expanded version */}
          <div style={{
            padding: sidebarCollapsed ? '16px 0' : '16px',
            borderTop: `1px solid ${theme.sidebarBorder}`,
            background: currentTheme === 'blue' ? 'rgba(0,0,0,0.2)' : theme.filterSection,
            textAlign: sidebarCollapsed ? 'center' : 'left'
          }}>
            {!sidebarCollapsed ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.accent}, ${theme.logoBg})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#fff',
                  flexShrink: 0,
                }}>
                  {employeeName ? initials(employeeName) : 'N'}
                </div>
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ fontSize: '11px', color: theme.sidebarTextMuted, fontWeight: '500' }}>
                    Clinic
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: theme.sidebarText,
                    fontWeight: '600',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>{employeeName}</div>
                </div>
              </div>
            ) : (
              <div
                style={{ fontSize: '14px', color: theme.sidebarText, fontWeight: '500', cursor: 'pointer', position: 'relative' }}
                onMouseEnter={() => setHoveredNavItem('user')}
                onMouseLeave={() => setHoveredNavItem(null)}
              >
                👤
                {hoveredNavItem === 'user' && (
                  <div style={{ ...styles(theme).tooltip, left: '100%' }}>
                    {employeeName} - Nurse
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>

        {/* ─── Main Content ─────────────────────────────────────────────── */}
        <main style={{
          flex: 1,
          marginLeft: sidebarCollapsed ? '80px' : '260px',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          transition: 'margin-left 0.3s ease',
           paddingTop: '80px', 
        }}>
          {/* Topbar with theme */}
          <Topbar token={urlToken} themeColor={currentTheme} />

          {/* Secondary Topbar */}
          <div style={{
            background: theme.cardBg,
            borderBottom: `1px solid ${theme.cardBorder}`,
            padding: '16px 28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: '60px',
            zIndex: 50,
          }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: theme.textPrimary }}>
              Family Planning Dashboard
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <LiveClock />
              <button
                className={isRefreshing ? 'spinning' : ''}
                onClick={() => refreshAll(true)}
                style={{
                  background: theme.tableHeader,
                  border: `1px solid ${theme.cardBorder}`,
                  color: theme.textSecondary,
                  padding: '7px 14px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = theme.textMuted}
                onMouseLeave={e => e.currentTarget.style.borderColor = theme.cardBorder}
              >
                <span style={{ fontSize: '14px' }}>↻</span>
                {isRefreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>

          {/* Content */}
          <div style={{ flex: 1, padding: '28px', display: 'flex', flexDirection: 'column', gap: '24px' }}>

            {/* Stats Row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '16px',
              className: 'stats-grid'
            }}>
              <div className="stat-card" style={styles(theme).statCard}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: theme.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>
                  Waiting (FP Triage)
                </div>
                <div style={{ fontSize: '32px', fontWeight: '800', color: theme.accent, lineHeight: 1 }}>
                  {waitingCount}
                </div>
                <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '6px' }}>
                  Clients pending method selection
                </div>
              </div>

              <div className="stat-card" style={styles(theme).statCard}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: theme.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>
                  Recent Clients
                </div>
                <div style={{ fontSize: '32px', fontWeight: '800', color: theme.info, lineHeight: 1 }}>
                  {clientStatuses.length}
                </div>
                <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '6px' }}>
                  Today's family planning visits
                </div>
              </div>

              <div className="stat-card" style={styles(theme).statCard}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: theme.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>
                  Action Required
                </div>
                <div style={{ fontSize: '32px', fontWeight: '800', color: theme.danger, lineHeight: 1 }}>
                  {giveCount}
                </div>
                <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '6px' }}>
                  Need medication dispensed
                </div>
              </div>

              <div className="stat-card" style={styles(theme).statCard}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: theme.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>
                  Completed
                </div>
                <div style={{ fontSize: '32px', fontWeight: '800', color: theme.warning, lineHeight: 1 }}>
                  {doneCount}
                </div>
                <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '6px' }}>
                  Fully processed today
                </div>
              </div>
            </div>

            {/* Error Banner */}
            {errorMessage && (
              <div style={{
                background: theme.dangerLight,
                border: `1px solid ${theme.danger}`,
                borderRadius: '8px',
                padding: '10px 14px',
                fontSize: '13px',
                color: theme.danger,
              }}>
                ⚠ {errorMessage}
              </div>
            )}

            {/* Columns Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px',
              flex: 1,
              className: 'columns-grid'
            }}>

              {/* Left: FP Waiting */}
              <div style={styles(theme).panel}>
                <div style={styles(theme).panelHeader}>
                  <div style={styles(theme).panelTitle}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: theme.accent, flexShrink: 0 }} />
                    FP Clients from Triage
                  </div>
                  <span style={{
                    fontSize: '12px',
                    color: theme.textMuted,
                    background: theme.tableHeader,
                    padding: '3px 10px',
                    borderRadius: '20px',
                    border: `1px solid ${theme.cardBorder}`,
                  }}>
                    {waitingCount} waiting
                  </span>
                </div>
                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '14px',
                  maxHeight: '520px',
                }}>
                  {fpPatients.length === 0 ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '48px 24px',
                      color: theme.textMuted,
                      gap: '10px',
                    }}>
                      <div style={{ fontSize: '32px', opacity: 0.4 }}>🩺</div>
                      <div style={{ fontSize: '14px' }}>No patients in the waiting queue</div>
                    </div>
                  ) : (
                    fpPatients.map(p => (
                      <div
                        key={p?.fp_id || Math.random()}
                        className="patient-card"
                        style={styles(theme).patientCard}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '10px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: theme.textPrimary, marginBottom: '4px' }}>
                              {p?.first_name || '—'} {p?.last_name || ''}
                            </div>
                            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                              {p?.age && (
                                <span style={{ fontSize: '12px', color: theme.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  👤 {p.age} yrs
                                </span>
                              )}
                              {p?.sex && (
                                <span style={{ fontSize: '12px', color: theme.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  ⚧ {p.sex}
                                </span>
                              )}
                              {p?.phone_number && (
                                <span style={{ fontSize: '12px', color: theme.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  📞 {p.phone_number}
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            className="select-btn"
                            onClick={() => setSelectedPatient(p)}
                            style={styles(theme).selectButton}
                          >
                            Select Method
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Right: Recent Clients */}
              <div style={styles(theme).panel}>
                <div style={styles(theme).panelHeader}>
                  <div style={styles(theme).panelTitle}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: theme.info, flexShrink: 0 }} />
                    Recent FP Clients
                  </div>
                  <span style={{
                    fontSize: '12px',
                    color: theme.textMuted,
                    background: theme.tableHeader,
                    padding: '3px 10px',
                    borderRadius: '20px',
                    border: `1px solid ${theme.cardBorder}`,
                  }}>
                    {clientStatuses.length} total
                  </span>
                </div>
                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '14px',
                  maxHeight: '520px',
                }}>
                  {clientStatuses.length === 0 ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '48px 24px',
                      color: theme.textMuted,
                      gap: '10px',
                    }}>
                      <div style={{ fontSize: '32px', opacity: 0.4 }}>📋</div>
                      <div style={{ fontSize: '14px' }}>No recent clients to display</div>
                    </div>
                  ) : (
                    clientStatuses.map(c => (
                      <div
                        key={c?.contact_id || Math.random()}
                        style={{
                          background: theme.cardBg,
                          border: `1px solid ${theme.cardBorder}`,
                          borderRadius: '8px',
                          padding: '14px 16px',
                          marginBottom: '10px',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <div style={{ fontWeight: '600', fontSize: '14px', color: theme.textPrimary }}>
                            {c?.first_name || '—'} {c?.last_name || ''}
                          </div>
                          {c?.method && (
                            <span style={{
                              fontSize: '11px',
                              fontWeight: '600',
                              background: theme.infoLight,
                              color: theme.info,
                              padding: '3px 10px',
                              borderRadius: '12px',
                              border: `1px solid ${theme.info}`,
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                            }}>
                              {c.method}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '8px' }}>
                          {c?.age && (
                            <span style={{ fontSize: '12px', color: theme.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                              👤 {c.age} yrs
                            </span>
                          )}
                          {c?.sex && (
                            <span style={{ fontSize: '12px', color: theme.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                              ⚧ {c.sex}
                            </span>
                          )}
                        </div>
                        {c?.status?.includes('Give') ? (
                          <span
                            onClick={() => handleOpenModal(c)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '11px',
                              fontWeight: '600',
                              color: theme.danger,
                              background: theme.dangerLight,
                              padding: '4px 10px',
                              borderRadius: '12px',
                              border: `1px solid ${theme.danger}`,
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            ⚠ {c.status}
                          </span>
                        ) : (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: theme.accent,
                            background: theme.accentLight,
                            padding: '4px 10px',
                            borderRadius: '12px',
                            border: `1px solid ${theme.accent}`,
                          }}>
                            ✓ {c?.status || 'Complete'}
                          </span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* ─── Method Selection Modal ──────────────────────────────────────── */}
      {selectedPatient && (
        <div style={styles(theme).modalOverlay} onClick={() => setSelectedPatient(null)}>
          <div style={styles(theme).modal} onClick={e => e.stopPropagation()}>
            <div style={{
              padding: '20px 24px 16px',
              borderBottom: `1px solid ${theme.tableBorder}`,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '12px',
            }}>
              <div>
                <div style={{ fontSize: '16px', fontWeight: '700', color: theme.textPrimary }}>Select Family Planning Method</div>
                <div style={{ fontSize: '13px', color: theme.textMuted, marginTop: '3px' }}>
                  {selectedPatient?.first_name || ''} {selectedPatient?.last_name || ''}
                  {selectedPatient?.age ? ` · ${selectedPatient.age} yrs` : ''}
                </div>
              </div>
              <button
                onClick={() => setSelectedPatient(null)}
                style={{
                  background: theme.tableHeader,
                  border: `1px solid ${theme.cardBorder}`,
                  color: theme.textSecondary,
                  width: '30px',
                  height: '30px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = theme.textMuted}
                onMouseLeave={e => e.currentTarget.style.borderColor = theme.cardBorder}
              >
                ×
              </button>
            </div>
            <div style={{
              padding: '16px 24px 24px',
              maxHeight: '420px',
              overflowY: 'auto',
            }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: theme.textMuted,
                marginBottom: '10px',
              }}>
                Available Methods
              </div>
              {familyPlanningMethods.length === 0 ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '32px 24px',
                  color: theme.textMuted,
                  gap: '10px',
                }}>
                  <div style={{ fontSize: '32px', opacity: 0.4 }}>💊</div>
                  <div style={{ fontSize: '14px' }}>No methods loaded</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {familyPlanningMethods.map((m, i) => (
                    <div
                      key={i}
                      className="method-row"
                      onClick={() => handleMethodSelect(m?.name)}
                      style={styles(theme).methodRow}
                    >
                      <span style={{ fontSize: '14px', fontWeight: '500', color: theme.textPrimary }}>
                        {m?.name || 'Unknown'}
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: theme.accent }}>
                        UGX {((m?.price) || 0).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Give FP Modal ──────────────────────────────────────────────── */}
      {showModal && modalData && (
        <GiveFPmodal
          clientDetails={modalData}
          onClose={() => {
            setShowModal(false);
            setModalData(null);
            refreshAll();
          }}
        />
      )}
    </>
  );
}

export default NurseDashboard;